// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/CampaignChatDetailHeader.test.tsx
import {
  ArrowLeft,
  CircleNotch,
  UploadSimple,
  Users,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

/**
 * Header bar shown above an embedded project chat (My chats → detail). Mirrors
 * the standalone Ask Puntt chat chrome: a back link to the chat list, the chat
 * title, and the sole "Share to project" action for private chats (the privacy
 * banner below is copy-only). Once shared, the action is replaced by a "Shared"
 * pill.
 */
export function CampaignChatDetailHeader({
  title,
  isShared,
  onBack,
  onShare,
  isSharing = false,
  shareDisabled = false,
}: {
  title: string;
  isShared: boolean;
  onBack: () => void;
  onShare: () => void;
  isSharing?: boolean;
  /** When true, the Share action is disabled (e.g. draft chat not yet created). */
  shareDisabled?: boolean;
}) {
  const { t } = useTranslation("ui");

  return (
    <header
      className="flex h-12 shrink-0 items-center gap-3 border-b border-puntt-neutral-gray-5 px-4"
      data-testid="campaign-chat-detail-header"
    >
      <button
        type="button"
        onClick={onBack}
        aria-label={t("campaignChatDetailHeader.backAriaLabel")}
        data-testid="campaign-chat-back"
        className="flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 text-[13px] font-medium text-puntt-neutral-gray-11 transition-colors hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-12"
      >
        <ArrowLeft size={16} />
        {t("campaignChatDetailHeader.backLabel")}
      </button>
      <span aria-hidden="true" className="text-puntt-neutral-gray-7">
        /
      </span>
      <h2
        className="min-w-0 flex-1 truncate text-[14px] font-semibold text-puntt-neutral-gray-12"
        data-testid="campaign-chat-detail-title"
      >
        {title}
      </h2>
      {isShared ? (
        <span
          className="flex shrink-0 items-center gap-1.5 rounded-md bg-puntt-neutral-gray-3 px-2.5 py-1.5 text-[13px] font-medium text-puntt-neutral-gray-11"
          data-testid="campaign-chat-shared-pill"
        >
          <Users size={15} aria-hidden="true" />
          {t("campaignChatDetailHeader.sharedWithProject")}
        </span>
      ) : (
        <button
          type="button"
          onClick={onShare}
          disabled={isSharing || shareDisabled}
          aria-label={t("campaignChatDetailHeader.shareButtonAriaLabel")}
          data-testid="campaign-chat-share-button"
          className="flex shrink-0 items-center gap-1.5 rounded-md bg-puntt-accent-9 px-2.5 py-1.5 text-[13px] font-medium text-base-white transition-colors hover:bg-puntt-accent-10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSharing ? (
            <CircleNotch size={15} weight="bold" className="animate-spin" />
          ) : (
            <UploadSimple size={15} weight="bold" />
          )}
          {t("campaignChatDetailHeader.shareButtonLabel")}
        </button>
      )}
    </header>
  );
}
